"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumSchematic = void 0;
const AltiumComponent_1 = require("./AltiumComponent");
const AltiumHarnessConnector_1 = require("./AltiumHarnessConnector");
const AltiumImplementationList_1 = require("./AltiumImplementationList");
const AltiumSheetSymbol_1 = require("./AltiumSheetSymbol");
const AltiumSheet_1 = require("./AltiumSheet");
const SchDocTypes_1 = require("../SchDocTypes");
class AltiumSchematic {
    constructor(version, fileName, storage, records, processingResults = []) {
        this.version = version;
        this.fileName = fileName;
        this.storage = storage;
        this.records = records;
        this.processingResults = processingResults;
        this._childRecordsByOwnerIndex = this.buildChildRecordsByOwnerIndex();
        const allComponents = this.records
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPONENT)
            .map((r) => new AltiumComponent_1.AltiumComponent(this, r));
        this._components = this.excludeMaskedComponents(allComponents);
        this._componentsByIndex = new Map(this._components.map((c) => [c.record.index, c]));
        this._componentsByUniqueId = new Map(this._components.map((c) => [c.record.uniqueId, c]));
        this._componentsByDesignator = new Map(this._components.reduce((acc, component) => {
            const designator = component.getDesignatorText();
            if (designator) {
                const existingComponents = acc.get(designator) ?? [];
                acc.set(designator, [...existingComponents, component]);
            }
            return acc;
        }, new Map()));
        this._sheetSymbols = this.records
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.SHEET_SYMBOL)
            .map((r) => new AltiumSheetSymbol_1.AltiumSheetSymbol(this, r));
        this._sheetSymbolsByIndex = new Map(this._sheetSymbols.map((s) => [s.record.index, s]));
        this._harnessConnectors = this.records
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR)
            .map((r) => new AltiumHarnessConnector_1.AltiumHarnessConnector(this, r));
        this._harnessConnectorsByIndex = new Map(this._harnessConnectors.map((h) => [h.record.index, h]));
        this._harnessConnectorChildrenByIndex = this.buildHarnessConnectorChildrenByIndex();
        this._implementationLists = this.records
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.IMPLEMENTATION_LIST)
            .map((r) => new AltiumImplementationList_1.AltiumImplementationList(this, r));
        this._implementationListsByIndex = new Map(this._implementationLists.map((i) => [i.record.index, i]));
        const sheetRecord = this.records.find((r) => r.recordType === SchDocTypes_1.RecordType.SHEET);
        this._sheet = sheetRecord ? new AltiumSheet_1.AltiumSheet(sheetRecord) : undefined;
    }
    getRecordByIndex(index) {
        return this.records.find((r) => r.index === index);
    }
    getChildRecords(ownerIndex) {
        return this._childRecordsByOwnerIndex.get(ownerIndex) ?? [];
    }
    getComponents() {
        return this._components;
    }
    getComponentByIndex(index) {
        return this._componentsByIndex.get(index);
    }
    getComponentByUniqueId(uniqueId) {
        return this._componentsByUniqueId.get(uniqueId);
    }
    getComponentsByDesignator(designator) {
        return this._componentsByDesignator.get(designator);
    }
    getSheetSymbols() {
        return this._sheetSymbols;
    }
    getSheetSymbolByIndex(index) {
        const symbol = this._sheetSymbolsByIndex.get(index);
        if (!symbol) {
            throw new Error(`Sheet symbol not found with index: ${index}`);
        }
        return symbol;
    }
    getSheetSymbolByUniqueId(uniqueId) {
        if (!this._sheetSymbolsByUniqueId) {
            this._sheetSymbolsByUniqueId = new Map(this._sheetSymbols.map((s) => [s.uniqueId, s]));
        }
        const symbol = this._sheetSymbolsByUniqueId.get(uniqueId);
        if (!symbol) {
            throw new Error(`Sheet symbol not found with unique ID: ${uniqueId}`);
        }
        return symbol;
    }
    getWires() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.WIRE);
    }
    getBuses() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.BUS);
    }
    getBusEntries() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.BUS_ENTRY);
    }
    getNetLabels() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.NET_LABEL);
    }
    getPowerPorts() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.POWER_PORT);
    }
    getJunctions() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.JUNCTION);
    }
    getGraphics() {
        return this.records.filter(SchDocTypes_1.isGraphicRecord);
    }
    getTemplates() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.TEMPLATE);
    }
    getHarnessConnectors() {
        return this._harnessConnectors;
    }
    getHarnessConnectorByIndex(index) {
        return this._harnessConnectorsByIndex.get(index);
    }
    getHarnessConnectorChildren(connectorIndex) {
        return this._harnessConnectorChildrenByIndex.get(connectorIndex) ?? this.getChildRecords(connectorIndex);
    }
    getHarnessDefinitionEntries(harnessType) {
        const foldedHarnessType = harnessType.toLowerCase();
        const connector = this._harnessConnectors.find((h) => h.getHarnessTypeText()?.toLowerCase() === foldedHarnessType);
        if (!connector) {
            return [];
        }
        const entries = connector.getEntries();
        const definitionEntries = entries.filter((entry) => this.isAdditionalListHarnessChild(entry));
        return definitionEntries.length > 0 ? definitionEntries : entries;
    }
    getHarnessDefinitionMembers(harnessType) {
        return this.getHarnessDefinitionEntries(harnessType).map((entry) => entry.name);
    }
    getImplementationLists() {
        return this._implementationLists;
    }
    getImplementationListByIndex(index) {
        const implementationList = this._implementationListsByIndex.get(index);
        if (!implementationList) {
            throw new Error(`Implementation list not found with index: ${index}`);
        }
        return implementationList;
    }
    getPorts() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.PORT);
    }
    getSignalHarnesses() {
        return this.records.filter((r) => r.recordType === SchDocTypes_1.RecordType.SIGNAL_HARNESS);
    }
    tryGetSheet() {
        return this._sheet;
    }
    getSheet() {
        if (!this._sheet) {
            throw new Error(`No sheet record found in schematic`);
        }
        return this._sheet;
    }
    getCompileMaskRects() {
        return this.records
            .filter((r) => r.recordType === SchDocTypes_1.RecordType.COMPILE_MASK)
            .map((mask) => ({
            minX: Math.min(mask.location.x, mask.corner.x),
            maxX: Math.max(mask.location.x, mask.corner.x),
            minY: Math.min(mask.location.y, mask.corner.y),
            maxY: Math.max(mask.location.y, mask.corner.y),
        }));
    }
    excludeMaskedComponents(components) {
        const rects = this.getCompileMaskRects();
        if (rects.length === 0) {
            return components;
        }
        return components.filter((component) => {
            const displayMode = component.record.displayMode ?? 0;
            const activePins = component
                .getPins()
                .filter((pin) => (pin.ownerPartId === -1 || pin.ownerPartId === component.record.currentPartId) &&
                pin.ownerPartDisplayMode !== -1 &&
                pin.ownerPartDisplayMode === displayMode);
            const points = [component.record.location, ...activePins.map((pin) => pin.location)];
            const enclosed = rects.some((rect) => points.every((pt) => pt.x >= rect.minX && pt.x <= rect.maxX && pt.y >= rect.minY && pt.y <= rect.maxY));
            return !enclosed;
        });
    }
    buildHarnessConnectorChildrenByIndex() {
        const childrenByConnector = new Map();
        const connectorsByIndex = new Map();
        for (const record of this.records) {
            if (record.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR) {
                connectorsByIndex.set(record.index, record);
            }
        }
        let currentConnector;
        for (const record of this.records) {
            if (record.recordType === SchDocTypes_1.RecordType.HARNESS_CONNECTOR) {
                currentConnector = record;
                if (!childrenByConnector.has(record.index)) {
                    childrenByConnector.set(record.index, []);
                }
                continue;
            }
            if (record.recordType !== SchDocTypes_1.RecordType.HARNESS_ENTRY && record.recordType !== SchDocTypes_1.RecordType.HARNESS_TYPE) {
                continue;
            }
            const explicitOwner = connectorsByIndex.get(record.ownerIndex);
            const owner = explicitOwner ?? currentConnector;
            if (!owner) {
                continue;
            }
            const children = childrenByConnector.get(owner.index) ?? [];
            children.push(record);
            childrenByConnector.set(owner.index, children);
        }
        return childrenByConnector;
    }
    buildChildRecordsByOwnerIndex() {
        const childrenByOwner = new Map();
        for (const record of this.records) {
            const children = childrenByOwner.get(record.ownerIndex) ?? [];
            children.push(record);
            childrenByOwner.set(record.ownerIndex, children);
        }
        return childrenByOwner;
    }
    isAdditionalListHarnessChild(record) {
        return record.properties.ownerindexadditionallist?.value?.toUpperCase() === 'T';
    }
}
exports.AltiumSchematic = AltiumSchematic;
//# sourceMappingURL=AltiumSchematic.js.map